import { FC } from 'react';

import { GiftDeliveryStatus } from '@shared/api/giftDelivery/giftDelivery.interface';
import { singleImageSelectHandlerWithPreview } from '@shared/utils/fileUtils';

import {
  statusLabel,
  prevStatusMap,
  prevStatusLabel,
} from '../utils/delivery.constants';
import { StatusModalState } from '../utils/delivery.types';

interface Props {
  modal: StatusModalState;
  setModal: React.Dispatch<React.SetStateAction<StatusModalState | null>>;
  revertingId: string | null;
  onConfirm: () => void;
  onRevert: (id: string, currentStatus: GiftDeliveryStatus) => Promise<void>;
  onSaveShipping: () => Promise<void>;
}

export const StatusChangeModal: FC<Props> = ({
  modal,
  setModal,
  revertingId,
  onConfirm,
  onRevert,
  onSaveShipping,
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={() => setModal(null)}
      />
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-gray-900">
          {modal.nextStatus === GiftDeliveryStatus.Preparing && '발주 처리'}
          {modal.nextStatus === GiftDeliveryStatus.Shipping && '송장 입력'}
          {modal.nextStatus === GiftDeliveryStatus.Delivered && '배송완료 처리'}
          {modal.isBulk ? ` (${modal.deliveryIds.length}건)` : ''}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
              statusLabel[modal.currentStatus]?.className || ''
            }`}
          >
            {statusLabel[modal.currentStatus]?.text}
          </span>
          {' → '}
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
              statusLabel[modal.nextStatus]?.className || ''
            }`}
          >
            {statusLabel[modal.nextStatus]?.text}
          </span>
        </p>

        {modal.nextStatus === GiftDeliveryStatus.Preparing && (
          <div className="mt-4 rounded-lg bg-yellow-50 p-3">
            <p className="text-sm text-yellow-800">발주처에 발주를 넣으시겠습니까?</p>
            <p className="mt-1 text-xs text-yellow-600">
              상태가 &quot;준비중&quot;으로 변경됩니다.
            </p>
          </div>
        )}

        {modal.nextStatus === GiftDeliveryStatus.Shipping && (
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                택배사 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={modal.carrier}
                onChange={(e) =>
                  setModal((prev) =>
                    prev ? { ...prev, carrier: e.target.value } : null,
                  )
                }
                placeholder="예: CJ대한통운, 한진택배"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                운송장 번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={modal.trackingNumber}
                onChange={(e) =>
                  setModal((prev) =>
                    prev ? { ...prev, trackingNumber: e.target.value } : null,
                  )
                }
                placeholder="운송장 번호 입력"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                발송일시
              </label>
              <input
                type="datetime-local"
                value={modal.shippedAt}
                onChange={(e) =>
                  setModal((prev) =>
                    prev ? { ...prev, shippedAt: e.target.value } : null,
                  )
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
        )}

        {modal.nextStatus === GiftDeliveryStatus.Delivered && (
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                배송완료 일시
              </label>
              <input
                type="datetime-local"
                value={modal.deliveredAt}
                onChange={(e) =>
                  setModal((prev) =>
                    prev ? { ...prev, deliveredAt: e.target.value } : null,
                  )
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                배송완료 사진
              </label>
              <div className="mt-1">
                {modal.deliveredImagePreview ? (
                  <div className="relative">
                    <img
                      src={modal.deliveredImagePreview}
                      alt="미리보기"
                      className="h-40 w-full rounded-lg border object-contain"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setModal((prev) =>
                          prev
                            ? {
                                ...prev,
                                deliveredImageFile: null,
                                deliveredImagePreview: null,
                              }
                            : null,
                        )
                      }
                      className="absolute right-1 top-1 rounded-full bg-white p-1 shadow hover:bg-gray-100"
                    >
                      <svg
                        className="h-4 w-4 text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-6 transition-colors hover:border-indigo-400 hover:bg-indigo-50">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        singleImageSelectHandlerWithPreview(
                          e,
                          (file: File | null) =>
                            setModal((prev) =>
                              prev ? { ...prev, deliveredImageFile: file } : null,
                            ),
                          (preview: string | null) =>
                            setModal((prev) =>
                              prev
                                ? { ...prev, deliveredImagePreview: preview }
                                : null,
                            ),
                        );
                      }}
                    />
                    <div className="text-center">
                      <svg
                        className="mx-auto h-8 w-8 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                      </svg>
                      <p className="mt-1 text-xs text-gray-500">
                        클릭하여 사진 업로드
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={() => setModal(null)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={() => {
              if (
                modal.nextStatus === GiftDeliveryStatus.Shipping &&
                (!modal.carrier || !modal.trackingNumber)
              ) {
                alert('택배사와 운송장 번호를 입력해주세요.');
                return;
              }
              onConfirm();
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
              modal.nextStatus === GiftDeliveryStatus.Preparing
                ? 'bg-yellow-600 hover:bg-yellow-700'
                : modal.nextStatus === GiftDeliveryStatus.Shipping
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {modal.nextStatus === GiftDeliveryStatus.Preparing && '발주 확인'}
            {modal.nextStatus === GiftDeliveryStatus.Shipping && '배송중 처리'}
            {modal.nextStatus === GiftDeliveryStatus.Delivered && '배송완료 확인'}
          </button>
        </div>

        {!modal.isBulk && (
          <div className="mt-4 border-t pt-4">
            {modal.currentStatus === GiftDeliveryStatus.Shipping && (
              <details className="group">
                <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                  현재 송장 정보 수정
                </summary>
                <div className="mt-3 space-y-2 rounded-lg bg-gray-50 p-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600">
                      택배사
                    </label>
                    <input
                      type="text"
                      value={modal.existingCarrier}
                      onChange={(e) =>
                        setModal((prev) =>
                          prev ? { ...prev, existingCarrier: e.target.value } : null,
                        )
                      }
                      className="mt-0.5 w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">
                      운송장 번호
                    </label>
                    <input
                      type="text"
                      value={modal.existingTrackingNumber}
                      onChange={(e) =>
                        setModal((prev) =>
                          prev
                            ? {
                                ...prev,
                                existingTrackingNumber: e.target.value,
                              }
                            : null,
                        )
                      }
                      className="mt-0.5 w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">
                      발송일시
                    </label>
                    <input
                      type="datetime-local"
                      value={modal.existingShippedAt}
                      onChange={(e) =>
                        setModal((prev) =>
                          prev
                            ? { ...prev, existingShippedAt: e.target.value }
                            : null,
                        )
                      }
                      className="mt-0.5 w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                  <button
                    onClick={async () => {
                      if (
                        !modal.existingCarrier.trim() ||
                        !modal.existingTrackingNumber.trim()
                      ) {
                        alert('택배사와 운송장 번호를 입력해주세요.');
                        return;
                      }
                      await onSaveShipping();
                    }}
                    className="w-full rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                  >
                    송장 정보 저장
                  </button>
                </div>
              </details>
            )}

            {!!prevStatusMap[modal.currentStatus] && (
              <div
                className={`${
                  modal.currentStatus === GiftDeliveryStatus.Shipping
                    ? 'mt-3'
                    : ''
                }`}
              >
                {!modal.showRevert ? (
                  <button
                    onClick={() =>
                      setModal((prev) =>
                        prev ? { ...prev, showRevert: true } : null,
                      )
                    }
                    className="text-xs text-red-500 underline hover:text-red-700"
                  >
                    {prevStatusLabel[modal.currentStatus] ||
                      '이전 상태로 되돌리기'}
                  </button>
                ) : (
                  <div className="space-y-2 rounded-lg bg-red-50 p-3">
                    <p className="text-xs font-medium text-red-700">
                      정말 이전 상태로 되돌리시겠습니까?
                    </p>
                    <p className="text-xs text-red-600">
                      확인을 위해 <span className="font-bold">되돌리기</span>를
                      입력해주세요.
                    </p>
                    <input
                      type="text"
                      value={modal.revertConfirmText}
                      onChange={(e) =>
                        setModal((prev) =>
                          prev
                            ? { ...prev, revertConfirmText: e.target.value }
                            : null,
                        )
                      }
                      placeholder="되돌리기"
                      className="w-full rounded border border-red-300 px-2 py-1.5 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          setModal((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  showRevert: false,
                                  revertConfirmText: '',
                                }
                              : null,
                          )
                        }
                        className="rounded border px-3 py-1 text-xs text-gray-600 hover:bg-gray-100"
                      >
                        취소
                      </button>
                      <button
                        onClick={() => {
                          const targetId = modal.deliveryIds[0];
                          if (!targetId) return;
                          onRevert(targetId, modal.currentStatus).then(() =>
                            setModal(null),
                          );
                        }}
                        disabled={
                          modal.revertConfirmText !== '되돌리기' ||
                          revertingId === modal.deliveryIds[0]
                        }
                        className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {revertingId === modal.deliveryIds[0]
                          ? '처리중...'
                          : prevStatusLabel[modal.currentStatus] || '되돌리기'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
